<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :visible="show"
                   :disable-submit-btn="submitDisabled"
                   :title="title" :ok-title="$t('Submit')" modal-class="mobile-full-screen"
                   scrollable>
      <div class="row">
        <div class="col-md-4">
          <customer-select-input-widget :model="model" is-search attribute="customer_uuid"
                                        :options="{placeholder: $t('Customer')}" :label="false"
                                        :disabled="customerPickerDisabled"/>

          <b-tooltip target="new-customer-btn" :title="$t('New Customer')"/>
        </div>
        <div class="col-md-2" style="padding-left: 1em">
          <b-tooltip target="new-customer-btn" :title="$t('New Customer')"/>
          <b-button variant="primary" id="new-customer-btn" @click="onNewCustomerClick"
                    :disabled="customerPickerDisabled">
            <font-awesome-icon icon="user-plus"/>
          </b-button>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <input-widget type="text" :model="model" attribute="customer_first_name"
                        :disabled="manualCustomerDisabled"
                        :placeholder="model.attributeLabels['customer_first_name']"
                        :label="false"
          />
        </div>
        <div class="col-md-4">
          <input-widget type="text" :model="model" attribute="customer_last_name" :disabled="manualCustomerDisabled"
                        :placeholder="model.attributeLabels['customer_last_name']"
                        :label="false"
          />
        </div>
        <div class="col-md-4">
          <input-widget type="text" :model="model" attribute="customer_email" :disabled="manualCustomerDisabled"
                        :placeholder="model.attributeLabels['customer_email']"
                        :options="{placeholder: $t('Customer Email')}"
                        :label="false" rules="email|max:255"
          />
        </div>
      </div>

      <artisio-table
          :fields="fields"
          :index="false"
          :items="model.reservations"
          requires-min-height
          :checkboxes="true"
          :enable-simple-search="false"
      >
        <template slot="cell(lot_no)" slot-scope="{item}">
          <span>
            {{ item.lot_no }}
          </span>
        </template>
        <template slot="cell(lot_title)" slot-scope="{item}">
          <span>
            {{ item.lot_title }}
          </span>
        </template>
        <template slot="cell(message)" slot-scope="{item}">
          <input-widget :model="item" type="textarea" attribute="message" :label="false" :rows="3"/>
        </template>
      </artisio-table>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import {
    ConditionReportRequestModalModel,
  } from "@/modules/inventory/condition-report-request-modal/ConditionReportRequestModal.model";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable.vue";
  import InputWidget from "@/core/components/input-widget/InputWidget.vue";
  import {Formatter} from "@/core/services/formatter.service";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget.vue";
  import {SOURCE_CONDITION_REPORT_REQUEST_MODAL} from "@/modules/constants";
  import ConditionReportRequestsService from "@/modules/condition-report-requests/ConditionReportRequestsService";
  import {LOTS_TABLE_CLEAR_SELECTED} from "@/plugins/event-bus/event-bus-names";
  import ReservationModel from "@/modules/inventory/Reservation.model";

  const {mapActions, mapState} = createNamespacedHelpers('inventory');
  const {mapActions: customerMapActions} = createNamespacedHelpers('customer');
  export default {
    name: "ConditionReportRequestModal",
    components: {
      CustomerSelectInputWidget,
      InputWidget,
      ArtisioTable,
      ArtisioModal,
    },
    data() {
      return {
        model: new ConditionReportRequestModalModel(),
        fields: [
          {key: "lot_no", label: this.$i18n.t('Lot')},
          {key: "lot_title", label: this.$i18n.t('Title')},
          {key: "message", label: this.$i18n.t('Message')}
        ]
      }
    },
    watch: {
      reservations() {
        this.model.reservations = this.reservations.map(el => new ReservationModel({
          uuid: el.uuid,
          lot_no: el.lot_no,
          lot_title: this.$options.filters.striptags(Formatter.attributeByMasterLanguage(el.inventory.dynamic_fields, 'title')),
          inventory_uuid: el.inventory.uuid,
          message: null
        }))
      },
      modal_customer_uuid() {
        this.model.customer_uuid = this.modal_customer_uuid
        this.model.customer = this.modal_customer
      },
    },
    computed: {
      ...mapState({
        show: state => state.conditionalReportRequestModal.show,
        modal_customer_uuid: state => state.conditionalReportRequestModal.object.customer_uuid,
        modal_customer: state => state.conditionalReportRequestModal.object.customer,
        reservations: state => state.conditionalReportRequestModal.reservations,
        modal: state => state.conditionalReportRequestModal
      }),
      title() {
        return this.$i18n.t("Create Condition Report Requests")
      },
      manualCustomerDisabled() {
        return !!this.model.customer_uuid
      },
      customerPickerDisabled() {
        return !!this.model.customer_first_name || !!this.model.customer_last_name || !!this.model.customer_email
      },
      submitDisabled() {
        return !this.model.customer_uuid && !this.manualCustomerEntered
      },
      manualCustomerEntered() {
        return this.model.customer_first_name && this.model.customer_last_name && this.model.customer_email
      }
    },
    methods: {
      ...customerMapActions({
        showCustomerModal: 'showModal'
      }),
      ...mapActions(["hideConditionalReportRequestModal"]),
      onHidden() {
        this.model = new ConditionReportRequestModalModel();
        this.hideConditionalReportRequestModal()
      },
      async onSubmit() {
        let request = null
        if (this.manualCustomerEntered) {
          const st = this.model.customer_first_name
          const nd = this.model.customer_last_name
          const mail = this.model.customer_email
          request = this.model.reservations.map(el => ({
            inventory_uuid: el.inventory_uuid,
            message: el.message,
            customer_first_name: st,
            customer_last_name: nd,
            customer_email: mail,
            reservation_uuid: el.uuid
          }))
        } else {
          request = this.model.reservations.map(el => ({
            inventory_uuid: el.inventory_uuid,
            message: el.message,
            customer_uuid: this.model.customer_uuid,
            reservation_uuid: el.uuid
          }))
        }

        const response = await ConditionReportRequestsService.createConditionReportRequest(request)
        if (response.success) {
          this.$toast('success', this.$i18n.t('Condition Report Requests were successfully created'));
          this.$evBus.$emit(LOTS_TABLE_CLEAR_SELECTED)
          this.hideConditionalReportRequestModal();
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.$toast('danger', this.$i18n.t('Something went wrong. Please try again.'));
          }
        }


      },
      onNewCustomerClick() {
        this.showCustomerModal({
          source: SOURCE_CONDITION_REPORT_REQUEST_MODAL
        })
      }
    }
  }
</script>

<style scoped>

</style>
